"use client"

import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { addDays, format, isBefore, isAfter, startOfDay } from "date-fns"
import { useCallback, useMemo, useState } from "react"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Skeleton } from "@/app/components/ui/skeleton"
import { cn } from "@/lib/utils"
import { observer } from "mobx-react-lite"
import { useStore } from "@/app/components/common/store.provider"
import { useBookingAvailability } from "@/lib/hooks/use-booking-availability"
import { isSlotInPastBusiness } from "@/lib/utils/business-timezone"
import { GroupGuestTabs, guestLabel } from "./GroupGuestTabs"

const DATE_STRIP_DAYS = 7

function toDateKey(d: Date): string {
    return format(d, "yyyy-MM-dd")
}

function formatTimeDisplay(time: string): string {
    const match = time.match(/^(\d{1,2}):(\d{2})/)
    if (!match) return time
    const h = parseInt(match[1], 10)
    const m = match[2]
    const h12 = h % 12 === 0 ? 12 : h % 12
    const ampm = h < 12 ? "AM" : "PM"
    return `${h12}:${m} ${ampm}`
}

export const Step2 = observer(function Step2() {
    const { appointmentStore } = useStore()
    const today = startOfDay(new Date())
    const businessTz = appointmentStore.businessTimezone
    const daysRange = appointmentStore.maxAvailableSlotsDays

    const [calendarOpen, setCalendarOpen] = useState(false)
    const [stripOffset, setStripOffset] = useState(0)

    const { availableDays, loadingDays, slots, loadingSlots } = useBookingAvailability()

    const isGroup = appointmentStore.isGroupMode
    const selectedDate = appointmentStore.activeDate
    const selectedStartTime = appointmentStore.activeStartTime
    const isCustomBooking = appointmentStore.activeIsCustomBooking
    const activeMember = appointmentStore.activeMember
    const activeMemberIndex = activeMember
        ? appointmentStore.groupMembers.findIndex((m) => m.localId === activeMember.localId)
        : -1
    const activeGuestName =
        activeMember && activeMemberIndex >= 0
            ? guestLabel(activeMember, activeMemberIndex)
            : ""

    const handleCalendarSelect = useCallback(
        (d: Date | undefined) => {
            if (!d) return
            const key = toDateKey(d)
            if (isBefore(d, today) || !availableDays.has(key)) return
            setCalendarOpen(false)
            appointmentStore.setActiveDate(key)
        },
        [appointmentStore, availableDays, today]
    )

    const isDateDisabled = useCallback(
        (d: Date) => {
            const key = toDateKey(d)
            const isPast = isBefore(d, today)
            const outOfRange = isAfter(d, addDays(today, daysRange - 1))
            return isPast || outOfRange || !availableDays.has(key)
        },
        [availableDays, today, daysRange]
    )

    const stripDates = useMemo(() => {
        const start = addDays(today, stripOffset * DATE_STRIP_DAYS)
        return Array.from({ length: DATE_STRIP_DAYS }, (_, i) => addDays(start, i))
    }, [stripOffset, today])

    const selectedDateObj = selectedDate ? new Date(`${selectedDate}T12:00:00`) : null

    const scrollStrip = (dir: "left" | "right") => {
        setStripOffset((prev) => (dir === "left" ? Math.max(0, prev - 1) : prev + 1))
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-foreground">Select time</h1>
                {isGroup ? (
                    <p className="mt-1 text-sm text-muted-foreground">
                        Everyone shares the same day. Pick a start time for each guest —
                        they can be staggered.
                    </p>
                ) : null}
            </div>

            {isGroup ? <GroupGuestTabs detail="time" /> : null}

            {isCustomBooking ? (
                <div className="rounded-lg border bg-muted/40 p-4 text-sm">
                    <p className="font-medium text-foreground">Custom booking request enabled</p>
                    <p className="mt-1 text-muted-foreground">
                        {isGroup
                            ? "You do not need to pick a day or times. The salon will schedule the whole party and confirm."
                            : "You do not need to select date or time. The admin will approve and assign your preferred slot."}
                    </p>
                </div>
            ) : (
            <>
            <div className="flex items-start gap-3">
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            variant="outline"
                            size="icon"
                            className="h-11 w-11 shrink-0 rounded-lg border"
                            aria-label="Open calendar"
                        >
                            <CalendarIcon className="h-5 w-5 text-muted-foreground" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent
                        className="w-[calc(100vw-2rem)] max-w-sm p-0 sm:w-auto"
                        align="start"
                        sideOffset={8}
                    >
                        <Calendar
                            mode="single"
                            selected={selectedDateObj ?? undefined}
                            onSelect={handleCalendarSelect}
                            disabled={isDateDisabled}
                            className="mx-auto"
                        />
                    </PopoverContent>
                </Popover>

                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-foreground">
                            {stripDates[0]
                                ? format(stripDates[0], "MMMM yyyy")
                                : format(today, "MMMM yyyy")}
                        </p>
                        <div className="flex gap-1">
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-11 w-11"
                                onClick={() => scrollStrip("left")}
                                disabled={stripOffset === 0}
                                aria-label="Previous week"
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-11 w-11"
                                onClick={() => scrollStrip("right")}
                                disabled={
                                    stripOffset >=
                                    Math.ceil(daysRange / DATE_STRIP_DAYS) - 1
                                }
                                aria-label="Next week"
                            >
                                <ChevronRight className="h-5 w-5" />
                            </Button>
                        </div>
                    </div>

                    <div className="flex gap-2 overflow-x-auto scroll-smooth scrollbar-hide py-2 sm:gap-3">
                        {loadingDays ? (
                            Array.from({ length: DATE_STRIP_DAYS }).map((_, i) => (
                                <Skeleton key={i} className="shrink-0 w-14 h-14 rounded-full sm:w-16 sm:h-16" />
                            ))
                        ) : (
                            stripDates.map((d) => {
                                const key = toDateKey(d)
                                const isDisabled = isBefore(d, today) || !availableDays.has(key)
                                const isSelected = selectedDate === key

                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => {
                                            if (isDisabled) return
                                            appointmentStore.setActiveDate(key)
                                        }}
                                        disabled={isDisabled}
                                        className={cn(
                                            "shrink-0 flex flex-col items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full border transition-colors",
                                            isSelected && "bg-primary text-primary-foreground border-primary",
                                            !isSelected && !isDisabled && "hover:bg-muted border-border",
                                            isDisabled && "opacity-50 cursor-not-allowed border-muted"
                                        )}
                                    >
                                        <span className="text-lg font-semibold">{format(d, "d")}</span>
                                        <span className="text-[10px] uppercase">{format(d, "EEE")}</span>
                                    </button>
                                )
                            })
                        )}
                    </div>
                </div>
            </div>

            <div>
                <h2 className="text-sm font-medium text-foreground mb-2">
                    {isGroup && activeGuestName
                        ? `Available times for ${activeGuestName}`
                        : "Available times"}
                </h2>
                {isGroup && (activeMember?.services.length ?? 0) === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        Pick services for this guest first to see the times that fit.
                    </p>
                ) : loadingSlots ? (
                    <div className="flex flex-col gap-2">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <Skeleton key={i} className="h-12 rounded-lg" />
                        ))}
                    </div>
                ) : !selectedDate ? (
                    <p className="text-sm text-muted-foreground">Select a date to see available times.</p>
                ) : slots.length === 0 ? (
                    <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">No slots available for this date.</p>
                        <Button
                            type="button"
                            variant="outline"
                            className="min-h-11 w-full sm:w-auto"
                            onClick={() => appointmentStore.setActiveIsCustomBooking(true)}
                        >
                            Switch to custom booking
                        </Button>
                    </div>
                ) : (
                    <div className="flex flex-col gap-2">
                        {slots.map((slot) => {
                            const isPastSlot = isSlotInPastBusiness(
                                slot.startTime,
                                selectedDate,
                                businessTz,
                            )
                            const isDisabled = !slot.isAvailable || isPastSlot
                            const isSelected = selectedStartTime === slot.startTime
                            const disabledReason =
                                isPastSlot
                                    ? "Past time"
                                    : slot.blockReason ??
                                      (slot.userHasConflict
                                          ? "You already have an appointment at this time"
                                          : !slot.isAvailable
                                            ? "Unavailable"
                                            : undefined)

                            return (
                                <button
                                    key={slot.startTime}
                                    type="button"
                                    onClick={() => {
                                        if (isDisabled) return
                                        appointmentStore.setActiveStartTime(slot.startTime)
                                    }}
                                    disabled={isDisabled}
                                    title={isDisabled ? disabledReason : undefined}
                                    className={cn(
                                        "flex flex-col items-start py-5 px-4 rounded-lg border text-lg font-medium transition-colors",
                                        isSelected && !isDisabled && "border-primary bg-primary/5 text-foreground",
                                        !isSelected && !isDisabled && "border-border hover:bg-muted",
                                        isDisabled && "opacity-50 cursor-not-allowed"
                                    )}
                                >
                                    <span>{formatTimeDisplay(slot.startTime)}</span>
                                    {isDisabled && disabledReason ? (
                                        <span className="text-xs font-normal text-muted-foreground mt-1">
                                            {disabledReason}
                                        </span>
                                    ) : null}
                                </button>
                            )
                        })}
                    </div>
                )}
            </div>
            </>
            )}
        </div>
    )
})
